// App.tsx — KTP site routes.
//
// Two classes of routes:
//   1. Custom hand-built pages (Home/Voyage, Learn concept map, Specs layer
//      map, Trust Tiers, Lineage, Blue Zone, Implement, Community, 404).
//      These keep the bespoke UI that expresses the framework visually.
//   2. Dynamic doc pages — every path declared in src/data/nav.ts resolves
//      to DocPage, which renders the corresponding markdown from the
//      synced docs/ folder and appends a cite-back footer.
//
// Router base is read from Vite's BASE_URL so GitHub Pages subpath deploys
// (/ktp-rfc/) and custom-domain root deploys (/) both work.
import PageLoadBoundary from "./components/PageLoadBoundary";
import { Redirect, Route, Router, Switch, useLocation } from "wouter";

/** The old /enterprise addresses, with their hashes kept. An old overview anchor (#ent-gates and its siblings) lives
 *  on the enterprise argument page now, not the hub; a sub-page keeps its own anchor (Stout's N1). */
function LegacyEnterprise({ slug }: { slug?: string }) {
  const hash = typeof window !== "undefined" ? window.location.hash : "";
  const to = slug ? `/governed-autonomy/${slug}` : hash ? "/governed-autonomy/enterprise" : "/governed-autonomy";
  return <Redirect to={`${to}${hash}`} replace />;
}
import { Suspense, useEffect, useLayoutEffect, useSyncExternalStore } from "react";
import { routePage } from "./lib/route-page";
import SiteHeader from "./components/SiteHeader";
import SiteFooter from "./components/SiteFooter";
import DesertFooter from "./components/DesertFooter";
import { isCompactFooter, isDesertPath } from "./lib/theme";
import { desertOverrideActive, subscribeDesertOverride } from "./lib/desert-override";
import ReadingProgress from "./components/ReadingProgress";
import ScrollToTop from "./components/ScrollToTop";
import RouteFocus from "./components/RouteFocus";
import DocumentTitle from "./components/DocumentTitle";
const Home = routePage(() => import("./pages/Home"));
const FindYourPath = routePage(() => import("./pages/FindYourPath"));
const About = routePage(() => import("./pages/About"));
const Btc = routePage(() => import("./pages/Btc"));
const Trust = routePage(() => import("./pages/Trust"));
const Learn = routePage(() => import("./pages/Learn"));
const TrustTiers = routePage(() => import("./pages/learn/TrustTiers"));
const Lineage = routePage(() => import("./pages/learn/Lineage"));
const Continuity = routePage(() => import("./pages/learn/Continuity"));
const Constitution = routePage(() => import("./pages/learn/Constitution"));
const DryDock = routePage(() => import("./pages/learn/DryDock"));
const Specs = routePage(() => import("./pages/Specs"));
const BlueZone = routePage(() => import("./pages/specs/BlueZone"));
const Implement = routePage(() => import("./pages/Implement"));
const Enterprise = routePage(() => import("./pages/Enterprise"));
const GovernedAutonomy = routePage(() => import("./pages/GovernedAutonomy"));
const GaOperatingModel = routePage(() => import("./pages/governed-autonomy/OperatingModel"));
const GaSemanticJoin = routePage(() => import("./pages/governed-autonomy/SemanticJoin"));
const GaKnowledgeGraph = routePage(() => import("./pages/governed-autonomy/KnowledgeGraph"));
const GaMissionJoin = routePage(() => import("./pages/governed-autonomy/MissionJoin"));
const GaWorkTypes = routePage(() => import("./pages/governed-autonomy/WorkTypes"));
const GaWorkforce = routePage(() => import("./pages/governed-autonomy/Workforce"));
const GaPlaybooks = routePage(() => import("./pages/governed-autonomy/Playbooks"));
const GaTests = routePage(() => import("./pages/governed-autonomy/Tests"));
const GaWritingRules = routePage(() => import("./pages/governed-autonomy/WritingRules"));
const GaGlossary = routePage(() => import("./pages/governed-autonomy/Glossary"));
const EnterpriseRisks = routePage(() => import("./pages/enterprise/EnterpriseRisks"));
const EnterpriseArchitecture = routePage(() => import("./pages/enterprise/EnterpriseArchitecture"));
const EnterpriseRoadmap = routePage(() => import("./pages/enterprise/EnterpriseRoadmap"));
const EnterpriseCiso = routePage(() => import("./pages/enterprise/EnterpriseCiso"));
const EnterpriseMethods = routePage(() => import("./pages/enterprise/EnterpriseMethods"));
const EnterpriseSkeptical = routePage(() => import("./pages/enterprise/EnterpriseSkeptical"));
const EnterpriseDefenseMap = routePage(() => import("./pages/enterprise/EnterpriseDefenseMap"));
const EnterpriseHomeGrown = routePage(() => import("./pages/enterprise/EnterpriseHomeGrown"));
const Research = routePage(() => import("./pages/Research"));
const Roadmap = routePage(() => import("./pages/research/Roadmap"));
const Papers = routePage(() => import("./pages/research/Papers"));
const Methods = routePage(() => import("./pages/research/Methods"));
const Falsification = routePage(() => import("./pages/research/Falsification"));
const BridgeMemoS5S6 = routePage(() => import("./pages/research/BridgeMemoS5S6"));
const CanYouMeasureTrust = routePage(() => import("./pages/research/CanYouMeasureTrust"));
const Shipwreck = routePage(() => import("./pages/research/Shipwreck"));
const IdentityAsAttractor = routePage(() => import("./pages/research/IdentityAsAttractor"));
const Ai2027 = routePage(() => import("./pages/research/Ai2027"));
const MagnificaHumanitas = routePage(() => import("./pages/research/MagnificaHumanitas"));
const ControlRoadmap = routePage(() => import("./pages/research/ControlRoadmap"));
const ExploitGym = routePage(() => import("./pages/research/ExploitGym"));
const AgiToAsi = routePage(() => import("./pages/research/AgiToAsi"));
const TheRfp = routePage(() => import("./pages/research/TheRfp"));
const AmbientBreach = routePage(() => import("./pages/research/AmbientBreach"));
const AmbientBreachSound = routePage(() => import("./pages/research/AmbientBreachSound"));
const FourClocks = routePage(() => import("./pages/research/FourClocks"));
const Federation = routePage(() => import("./pages/research/Federation"));
const Scenario = routePage(() => import("./pages/research/Scenario"));
const Quantum = routePage(() => import("./pages/research/Quantum"));
const CellCheck = routePage(() => import("./pages/research/CellCheck"));
const Collaborate = routePage(() => import("./pages/research/Collaborate"));
const Cartography = routePage(() => import("./pages/research/Cartography"));
const Wolfram = routePage(() => import("./pages/research/Wolfram"));
const Cognitive = routePage(() => import("./pages/research/Cognitive"));
const Dcti = routePage(() => import("./pages/research/Dcti"));
const InstalledWatcher = routePage(() => import("./pages/research/InstalledWatcher"));
const InstalledWatcherSources = routePage(() => import("./pages/research/InstalledWatcherSources"));
const Acpp = routePage(() => import("./pages/research/Acpp"));
const Ladder = routePage(() => import("./pages/research/Ladder"));
const ClassicalLineage = routePage(() => import("./pages/research/ClassicalLineage"));
const Violence = routePage(() => import("./pages/research/Violence"));
const PriorArt = routePage(() => import("./pages/research/PriorArt"));
const CoePrimer = routePage(() => import("./pages/research/CoePrimer"));
const Canon = routePage(() => import("./pages/canon/Hub"));
const CanonStack = routePage(() => import("./pages/canon/Stack"));
const CanonClaims = routePage(() => import("./pages/canon/Claims"));
const CanonLimits = routePage(() => import("./pages/canon/Limits"));
const CanonGlossary = routePage(() => import("./pages/canon/Glossary"));
const CanonEthics = routePage(() => import("./pages/canon/Ethics"));
const CanonSynthesis = routePage(() => import("./pages/canon/Synthesis"));
const CanonRepair = routePage(() => import("./pages/canon/Repair"));
const ReconciliationDynamics = routePage(() => import("./pages/canon/ReconciliationDynamics"));
const PlanAResponse = routePage(() => import("./pages/research/PlanAResponse"));
const SuspiciousMinds = routePage(() => import("./pages/research/SuspiciousMinds"));
const SuspiciousMindsStage = routePage(() => import("./pages/research/SuspiciousMindsStage"));
const TwoGates = routePage(() => import("./pages/TwoGates"));
const Audit = routePage(() => import("./pages/Audit"));
const Contributors = routePage(() => import("./pages/Contributors"));
const InMotionRecovery = routePage(() => import("./pages/InMotionRecovery"));
const InMotionBlocks = routePage(() => import("./pages/InMotionBlocks"));
import SearchHost from "./components/SearchHost";
import { Analytics } from "@vercel/analytics/react";
const CanonClusters = routePage(() => import("./pages/canon/Clusters"));
const StewartBrand = routePage(() => import("./pages/canon/ancestors/StewartBrand"));
const TrustForceEquation = routePage(() => import("./pages/learn/TrustForceEquation"));
const Coordinates = routePage(() => import("./pages/learn/Coordinates"));
const CoordinateMapTool = routePage(() => import("./pages/learn/CoordinateMapTool"));
const Writing = routePage(() => import("./pages/Writing"));
const AuthorityWithoutGravity = routePage(() => import("./pages/writing/AuthorityWithoutGravity"));
const WhatWasGrowing = routePage(() => import("./pages/writing/WhatWasGrowing"));
const WhatItCosts = routePage(() => import("./pages/writing/WhatItCosts"));
const WhatRemains = routePage(() => import("./pages/writing/WhatRemains"));
const SixSeven = routePage(() => import("./pages/writing/SixSeven"));
const SoftenedGround = routePage(() => import("./pages/writing/SoftenedGround"));
const WhatWePlant = routePage(() => import("./pages/writing/WhatWePlant"));
const WritingDoc = routePage(() => import("./pages/writing/WritingDoc"));
const Language = routePage(() => import("./pages/Language"));
const VocabDossier = routePage(() => import("./pages/language/VocabDossier"));
const Community = routePage(() => import("./pages/Community"));
const VoyageChapter = routePage(() => import("./pages/VoyageChapter"));
const Advocacy = routePage(() => import("./pages/Advocacy"));
const AdvocacyPlaybook = routePage(() => import("./pages/advocacy/Playbook"));
const AdvocacyChildSafetyPledge = routePage(() => import("./pages/advocacy/ChildSafetyPledge"));
const AdvocacySubstrates = routePage(() => import("./pages/advocacy/Substrates"));
const AdvocacyFamily = routePage(() => import("./pages/advocacy/Family"));
const AdvocacyCulture = routePage(() => import("./pages/advocacy/Culture"));
const AdvocacyMeasurement = routePage(() => import("./pages/advocacy/Measurement"));
const AdvocacyLaw = routePage(() => import("./pages/advocacy/Law"));
const AdvocacyMoney = routePage(() => import("./pages/advocacy/Money"));
const AdvocacyK12Hub = routePage(() => import("./pages/advocacy/K12Hub"));
const AdvocacyK12SubstrateMap = routePage(() => import("./pages/advocacy/k12/SubstrateMap"));
const AdvocacyMeasurementReform = routePage(() => import("./pages/advocacy/k12/MeasurementReform"));
const AdvocacyPhoneFreeZones = routePage(() => import("./pages/advocacy/k12/PhoneFreeZones"));
const AdvocacyInterpretivePractice = routePage(() => import("./pages/advocacy/k12/InterpretivePractice"));
const AdvocacySubstrateProvisioning = routePage(() => import("./pages/advocacy/k12/SubstrateProvisioning"));
const AdvocacyVoiceInDesign = routePage(() => import("./pages/advocacy/k12/VoiceInDesign"));
const AdvocacyAiLiteracy = routePage(() => import("./pages/advocacy/k12/AiLiteracy"));
const AdvocacyLadderCurriculum = routePage(() => import("./pages/advocacy/k12/LadderCurriculum"));
const AdvocacyAiGuidanceCrosswalk = routePage(() => import("./pages/advocacy/k12/AiGuidanceCrosswalk"));
const AdvocacyStormScale = routePage(() => import("./pages/advocacy/k12/StormScale"));
const AdvocacySfpsHub = routePage(() => import("./pages/advocacy/SfpsHub"));
const AdvocacySfpsParent1Pager = routePage(() => import("./pages/advocacy/SfpsParent1Pager"));
const AdvocacySfpsFileIpra = routePage(() => import("./pages/advocacy/SfpsFileIpra"));
const AdvocacySfpsThread = routePage(() => import("./pages/advocacy/SfpsThread"));
const AdvocacySfpsClocks = routePage(() => import("./pages/advocacy/SfpsClocks"));
const AdvocacySfpsScorecard = routePage(() => import("./pages/advocacy/SfpsScorecard"));
const AdvocacySfpsProposal = routePage(() => import("./pages/advocacy/SfpsProposal"));
const AdvocacySfpsSources = routePage(() => import("./pages/advocacy/SfpsSources"));
const AdvocacySfpsAmiraPolicyDiff = routePage(() => import("./pages/advocacy/SfpsAmiraPolicyDiff"));
const AdvocacySfpsLescHearing = routePage(() => import("./pages/advocacy/SfpsLescHearing"));
const AdvocacySfpsTheRecord = routePage(() => import("./pages/advocacy/SfpsTheRecord"));
const AdvocacySfpsRead = routePage(() => import("./pages/advocacy/SfpsRead"));
const AdvocacyTemplates = routePage(() => import("./pages/advocacy/Templates"));
const AdvocacyScoreboard = routePage(() => import("./pages/advocacy/Scoreboard"));
const AdvocacyKpis = routePage(() => import("./pages/advocacy/Kpis"));
const AdvocacyCoalition = routePage(() => import("./pages/advocacy/Coalition"));
const AdvocacyDisclose = routePage(() => import("./pages/advocacy/Disclose"));
const AdvocacyContribute = routePage(() => import("./pages/advocacy/Contribute"));
const AdvocacyDecisionTree = routePage(() => import("./pages/advocacy/DecisionTree"));
const AdvocacyOpposition = routePage(() => import("./pages/advocacy/Opposition"));
const AdvocacyExistingLaw = routePage(() => import("./pages/advocacy/ExistingLaw"));
const JourneyStart = routePage(() => import("./pages/JourneyStart"));
const Arrival = routePage(() => import("./pages/Arrival"));
const Disarm = routePage(() => import("./pages/Disarm"));
const DisarmMemo = routePage(() => import("./pages/DisarmMemo"));
const Enshittification = routePage(() => import("./pages/Enshittification"));
import JourneyRemarkBanner from "./components/JourneyRemarkBanner";
import JourneyStopWatcher from "./components/JourneyStopWatcher";
const DynamicDoc = routePage(() => import("./pages/DynamicDoc"));
const NotFound = routePage(() => import("./pages/NotFound"));
const FakeNotFound = routePage(() => import("./pages/FakeNotFound"));
const PhysicsHome = routePage(() => import("./pages/physics/Home"));
const AxiomList = routePage(() => import("./pages/physics/AxiomList"));
const Axiom = routePage(() => import("./pages/physics/Axiom"));
const PhysicsSlugRouter = routePage(() => import("./pages/physics/PhysicsSlugRouter"));
const InternalIndex = routePage(() => import("./pages/research/cognitive/InternalIndex"));
const InternalDoc = routePage(() => import("./pages/research/cognitive/InternalDoc"));

const BASE = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");

export default function App({ ssrPath }: { ssrPath?: string } = {}) {
  return (
    <Router base={BASE} ssrPath={ssrPath}>
      <AppShell />
    </Router>
  );
}

// useLayoutEffect warns under the prerender's server render; it only matters in the browser.
const useBrowserLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

// AppShell renders inside <Router> so it can read the current location and
// decide which chrome to show. The immersive /advocacy/sfps/read experience
// renders FULL-BLEED: the shared header, footer, journey strips, and the
// site's own reading-progress bar are all omitted for that one exact path so
// the scrollytelling world owns the viewport (it ships its own progress bar,
// theme toggle, and document-scroll behavior). Non-visual helpers
// (ScrollToTop, RouteFocus, SearchHost, Analytics, DocumentTitle,
// JourneyStopWatcher) stay mounted everywhere.
//
// The Desert Book theme (front door + stories) is a data-theme attribute on
// <html>, so it reaches the html/body background and portals. index.html and
// the prerender set it for first paint; this effect keeps it right across
// in-app navigation, before paint.
function AppShell() {
  const [location] = useLocation();
  const immersive = location === "/advocacy/sfps/read";
  // A page with no path of its own (the 404) raises the override while mounted; see lib/desert-override.ts.
  const desertOverride = useSyncExternalStore(subscribeDesertOverride, desertOverrideActive, () => false);
  const desert = isDesertPath(location) || desertOverride;
  useBrowserLayoutEffect(() => {
    const root = document.documentElement;
    if (desert) root.dataset.theme = "desert";
    else delete root.dataset.theme;
  }, [desert]);
  return (
    <>
      {/* Skip link — first focusable element on every page. Visually
          hidden until keyboard-focused (see .skip-link in tokens.css);
          jumps past the header/nav chrome straight to <main>. */}
      <a className="skip-link" href="#main-content">
        skip to content
      </a>
      <ScrollToTop />
      <RouteFocus />
      <SearchHost />
      <Analytics />
      <DocumentTitle />
      {/* Not on the front door: it isn't read through. */}
      {!immersive && location !== "/" && <ReadingProgress />}
      <JourneyStopWatcher />
      {!immersive && <SiteHeader />}
      {!immersive && <JourneyRemarkBanner />}
      {/* id + tabIndex(-1) make <main> the target for the skip link and
          for RouteFocus's programmatic focus on SPA navigation. outline
          suppressed — a focus ring around the whole page body would be
          noise; the global :focus-visible rule covers real controls. */}
      <main id="main-content" tabIndex={-1} style={{ flex: 1, outline: "none" }}>
        <PageLoadBoundary key={location}>
        <Suspense fallback={<div className="page" role="status" aria-live="polite">Loading page…</div>}>
        <Switch>
          {/* Primary entry — six authored paths. / IS the journey start.
              The original home page remains accessible at /home. */}
          <Route path="/" component={Arrival} />
          <Route path="/home" component={Home} />
          <Route path="/start" component={JourneyStart} />
          <Route path="/about" component={About} />
          <Route path="/find-your-path" component={FindYourPath} />
          <Route path="/btc" component={Btc} />
          <Route path="/trust" component={Trust} />
          <Route path="/two-gates" component={TwoGates} />
          <Route path="/audit" component={Audit} />
          <Route path="/audit/culture" component={Audit} />
          <Route path="/contributors" component={Contributors} />
          <Route path="/in-motion/recovery" component={InMotionRecovery} />
          <Route path="/in-motion/blocks" component={InMotionBlocks} />
          <Route path="/learn" component={Learn} />
          <Route path="/learn/trust-tiers" component={TrustTiers} />
          <Route path="/learn/lineage" component={Lineage} />
          <Route path="/learn/continuity" component={Continuity} />
          <Route path="/learn/constitution" component={Constitution} />
          <Route path="/learn/simulator" component={DryDock} />
          <Route path="/learn/trust-force-equation" component={TrustForceEquation} />
          <Route path="/learn/coordinates" component={Coordinates} />
          <Route path="/learn/coordinates/map" component={CoordinateMapTool} />
          <Route path="/specs" component={Specs} />
          <Route path="/specs/blue-zones" component={BlueZone} />
          <Route path="/implement" component={Implement} />
          <Route path="/disarm" component={Disarm} />
          <Route path="/disarm/memo" component={DisarmMemo} />
          <Route path="/enshittification" component={Enshittification} />
          {/* /enterprise became /governed-autonomy (Chris, 2026-10-27); vercel.json carries the same redirects for the host. */}
          <Route path="/enterprise"><LegacyEnterprise /></Route>
          <Route path="/enterprise/:slug">{(params) => <LegacyEnterprise slug={params.slug} />}</Route>
          <Route path="/governedautonomy"><Redirect to="/governed-autonomy" /></Route>
          <Route path="/governed-autonomy" component={GovernedAutonomy} />
          <Route path="/governed-autonomy/enterprise" component={Enterprise} />
          <Route path="/governed-autonomy/operating-model" component={GaOperatingModel} />
          <Route path="/governed-autonomy/semantic-join" component={GaSemanticJoin} />
          <Route path="/governed-autonomy/knowledge-graph" component={GaKnowledgeGraph} />
          <Route path="/governed-autonomy/mission-join" component={GaMissionJoin} />
          <Route path="/governed-autonomy/work-types" component={GaWorkTypes} />
          <Route path="/governed-autonomy/workforce" component={GaWorkforce} />
          <Route path="/governed-autonomy/playbooks" component={GaPlaybooks} />
          <Route path="/governed-autonomy/tests" component={GaTests} />
          <Route path="/governed-autonomy/writing-rules" component={GaWritingRules} />
          <Route path="/governed-autonomy/glossary" component={GaGlossary} />
          <Route path="/governed-autonomy/risks" component={EnterpriseRisks} />
          <Route path="/governed-autonomy/architecture" component={EnterpriseArchitecture} />
          <Route path="/governed-autonomy/roadmap" component={EnterpriseRoadmap} />
          <Route path="/governed-autonomy/ciso" component={EnterpriseCiso} />
          <Route path="/governed-autonomy/methods" component={EnterpriseMethods} />
          <Route path="/governed-autonomy/skeptical" component={EnterpriseSkeptical} />
          <Route path="/governed-autonomy/defense-map" component={EnterpriseDefenseMap} />
          <Route path="/governed-autonomy/home-grown" component={EnterpriseHomeGrown} />
          <Route path="/research" component={Research} />
          <Route path="/research/roadmap" component={Roadmap} />
          <Route path="/research/papers" component={Papers} />
          <Route path="/research/methods" component={Methods} />
          <Route path="/research/cartography" component={Cartography} />
          <Route path="/research/wolfram" component={Wolfram} />
          <Route path="/research/cognitive" component={Cognitive} />
          <Route path="/research/cognitive/dcti" component={Dcti} />
          <Route path="/research/cognitive/installed-watcher" component={InstalledWatcher} />
          <Route path="/research/cognitive/sources" component={InstalledWatcherSources} />
          <Route path="/research/cognitive/acpp" component={Acpp} />
          <Route path="/research/cognitive/ladder" component={Ladder} />
          <Route path="/research/cognitive/lineage" component={ClassicalLineage} />
          {/* Speakeasy — internal CDO doctrine. Not linked from the public site;
              not in public/sitemap.xml; not in scripts/inject-og.mjs ROUTES.
              Each page injects noindex/nofollow meta tags. Reachable only by
              typed URL. The instrument-vs-weapon boundary holds. */}
          <Route path="/research/cognitive/internal" component={InternalIndex} />
          <Route path="/research/cognitive/internal/:slug" component={InternalDoc} />
          <Route path="/research/falsification" component={Falsification} />
          <Route path="/research/falsification/bridge-memo" component={BridgeMemoS5S6} />
          <Route path="/research/can-you-measure-trust" component={CanYouMeasureTrust} />
          <Route path="/research/shipwreck" component={Shipwreck} />
          <Route path="/research/violence" component={Violence} />
          <Route path="/research/identity-as-attractor" component={IdentityAsAttractor} />
          <Route path="/research/ai-2027" component={Ai2027} />
          <Route path="/research/magnifica-humanitas" component={MagnificaHumanitas} />
          <Route path="/research/control-roadmap" component={ControlRoadmap} />
          <Route path="/research/exploitgym" component={ExploitGym} />
          <Route path="/research/agi-to-asi" component={AgiToAsi} />
          <Route path="/research/the-rfp" component={TheRfp} />
          <Route path="/research/plan-a" component={PlanAResponse} />
          <Route path="/research/ambient-breach" component={AmbientBreach} />
          <Route path="/research/ambient-breach/sound" component={AmbientBreachSound} />
          <Route path="/research/hear-the-comma">
            <Redirect to="/research/ambient-breach/sound" />
          </Route>
          <Route path="/research/sound-stage">
            <Redirect to="/research/ambient-breach/sound" />
          </Route>
          <Route path="/research/four-clocks" component={FourClocks} />
          <Route path="/research/federation" component={Federation} />
          <Route path="/research/scenario" component={Scenario} />
          <Route path="/research/quantum" component={Quantum} />
          <Route path="/research/cell-check" component={CellCheck} />
          <Route path="/research/collaborate" component={Collaborate} />
          <Route path="/research/prior-art" component={PriorArt} />
          <Route path="/research/coe-primer" component={CoePrimer} />
          <Route path="/research/suspicious-minds" component={SuspiciousMinds} />
          <Route path="/research/suspicious-minds/stage" component={SuspiciousMindsStage} />
          <Route path="/theater">
            <Redirect to="/research/suspicious-minds/stage" />
          </Route>
          <Route path="/canon" component={Canon} />
          <Route path="/canon/stack" component={CanonStack} />
          <Route path="/canon/claims" component={CanonClaims} />
          <Route path="/canon/limits" component={CanonLimits} />
          <Route path="/canon/glossary" component={CanonGlossary} />
          <Route path="/canon/ethics" component={CanonEthics} />
          <Route path="/canon/synthesis" component={CanonSynthesis} />
          <Route path="/canon/repair" component={CanonRepair} />
          <Route path="/canon/reconciliation-dynamics" component={ReconciliationDynamics} />
          <Route path="/canon/clusters" component={CanonClusters} />
          <Route path="/canon/ancestors/stewart-brand" component={StewartBrand} />
          <Route path="/language" component={Language} />
          <Route path="/language/:id">
            {(params) => <VocabDossier id={params?.id ?? ""} />}
          </Route>
          <Route path="/writing" component={Writing} />
          <Route path="/writing/authority-without-gravity" component={AuthorityWithoutGravity} />
          <Route path="/writing/what-was-growing" component={WhatWasGrowing} />
          <Route path="/writing/what-it-costs" component={WhatItCosts} />
          <Route path="/writing/what-remains" component={WhatRemains} />
          <Route path="/writing/six-seven" component={SixSeven} />
          <Route path="/writing/softened-ground" component={SoftenedGround} />
          <Route path="/writing/what-we-plant" component={WhatWePlant} />
          {/* All other /writing/:slug routes resolve to migrated markdown
              under src/content/writing/. Declared after the seven hand-built
              writing pages above so wouter matches the bespoke routes first. */}
          <Route path="/writing/:slug" component={WritingDoc} />
          <Route path="/community" component={Community} />
          <Route path="/voyage/:slug" component={VoyageChapter} />

          {/* /advocacy — Phase 1 K-12 launch (May 2026). Public catalog
              of substrate-level interventions with Tuesday-morning
              templates, public scoreboard, tiered coalition, and a
              structured contribution queue. Schema in
              src/data/advocacy/k12.ts; deep-page renderer in
              src/components/advocacy/InterventionPage.tsx. Hand-built
              routes match before any DynamicDoc fallback would. */}
          <Route path="/advocacy" component={Advocacy} />
          <Route path="/advocacy/playbook" component={AdvocacyPlaybook} />
          <Route path="/advocacy/child-safety-pledge" component={AdvocacyChildSafetyPledge} />
          <Route path="/advocacy/substrates" component={AdvocacySubstrates} />
          <Route path="/advocacy/family" component={AdvocacyFamily} />
          <Route path="/advocacy/culture" component={AdvocacyCulture} />
          <Route path="/advocacy/measurement" component={AdvocacyMeasurement} />
          <Route path="/advocacy/law" component={AdvocacyLaw} />
          <Route path="/advocacy/money" component={AdvocacyMoney} />
          <Route path="/advocacy/k12" component={AdvocacyK12Hub} />
          <Route path="/advocacy/k12/substrate-map" component={AdvocacyK12SubstrateMap} />
          <Route path="/advocacy/k12/measurement-reform" component={AdvocacyMeasurementReform} />
          <Route path="/advocacy/k12/phone-free-zones" component={AdvocacyPhoneFreeZones} />
          <Route path="/advocacy/k12/interpretive-practice" component={AdvocacyInterpretivePractice} />
          <Route path="/advocacy/k12/substrate-provisioning" component={AdvocacySubstrateProvisioning} />
          <Route path="/advocacy/k12/voice-in-design" component={AdvocacyVoiceInDesign} />
          <Route path="/advocacy/k12/ai-literacy/ladder" component={AdvocacyLadderCurriculum} />
          <Route path="/advocacy/k12/ai-literacy" component={AdvocacyAiLiteracy} />
          <Route path="/advocacy/k12/ai-guidance-crosswalk" component={AdvocacyAiGuidanceCrosswalk} />
          <Route path="/advocacy/k12/storm-scale" component={AdvocacyStormScale} />
          {/* /advocacy/sfps — "The Santa Fe AI" community campaign.
              Hub holds the frame, the disclaimer, the 2031 cohort
              live counter, the Four Clocks strip, and surfaces grid.
              Sub-routes ship per drafts/santa-fe-ai/PLAN.md §13. */}
          <Route path="/advocacy/sfps" component={AdvocacySfpsHub} />
          <Route path="/advocacy/sfps/parent-1pager" component={AdvocacySfpsParent1Pager} />
          <Route path="/advocacy/sfps/file-an-ipra" component={AdvocacySfpsFileIpra} />
          <Route path="/advocacy/sfps/thought-partner" component={AdvocacySfpsThread} />
          <Route path="/advocacy/sfps/clocks" component={AdvocacySfpsClocks} />
          <Route path="/advocacy/sfps/scorecard" component={AdvocacySfpsScorecard} />
          <Route path="/advocacy/sfps/proposal" component={AdvocacySfpsProposal} />
          <Route path="/advocacy/sfps/sources" component={AdvocacySfpsSources} />
          <Route path="/advocacy/sfps/amira-policy-diff" component={AdvocacySfpsAmiraPolicyDiff} />
          <Route path="/advocacy/sfps/lesc-hearing" component={AdvocacySfpsLescHearing} />
          <Route path="/advocacy/sfps/the-record" component={AdvocacySfpsTheRecord} />
          {/* Immersive full-bleed scrollytelling experience. App omits the
              shared chrome for this exact path (see AppShell below). */}
          <Route path="/advocacy/sfps/read" component={AdvocacySfpsRead} />
          <Route path="/advocacy/templates" component={AdvocacyTemplates} />
          <Route path="/advocacy/scoreboard" component={AdvocacyScoreboard} />
          <Route path="/advocacy/kpis" component={AdvocacyKpis} />
          <Route path="/advocacy/coalition" component={AdvocacyCoalition} />
          <Route path="/advocacy/disclose" component={AdvocacyDisclose} />
          <Route path="/advocacy/contribute" component={AdvocacyContribute} />
          <Route path="/advocacy/decision-tree" component={AdvocacyDecisionTree} />
          <Route path="/advocacy/opposition" component={AdvocacyOpposition} />
          <Route path="/advocacy/existing-law" component={AdvocacyExistingLaw} />

          {/* Speakeasy — not listed in nav, excluded from sitemap.
              /colophon is a fake 404 that hides the door to /physics.
              /physics is the eight-room home. Each room lives at its own
              slug; axioms has a dedicated index + per-axiom deep-dive.
              The /physics/:segment dispatcher both serves room slugs and
              redirects numeric axiom URLs (e.g. /physics/6) from Phase 2.

              NOTE: a few public pages (e.g. the Dry Dock simulator) link
              INTO /physics/acequia on purpose — the acequia is the canonical
              treatment of that material, and the link is a deliberate quiet
              door, not a leak. These public→/physics links are intentional;
              do not "fix" them in an audit. The speakeasy stays out of nav
              and sitemap; being link-reachable from a few curated spots is
              by design. */}
          <Route path="/colophon" component={FakeNotFound} />
          <Route path="/physics" component={PhysicsHome} />
          <Route path="/physics/axioms" component={AxiomList} />
          <Route path="/physics/axioms/:n" component={Axiom} />
          <Route path="/physics/:segment" component={PhysicsSlugRouter} />

          {/* Doc-rendered pages from src/content/ — matched in path order.
              /rfcs/* intentionally not hosted here; see RFCS_TXT_URL in nav.ts
              and Specs.tsx for the external canonical source. */}
          <Route path="/learn/:slug" component={DynamicDoc} />
          <Route path="/specs/:slug" component={DynamicDoc} />
          <Route path="/schemas" component={DynamicDoc} />
          <Route path="/schemas/:slug" component={DynamicDoc} />
          <Route path="/implement/:slug" component={DynamicDoc} />
          <Route path="/community/:slug" component={DynamicDoc} />
          <Route path="/community/writings/:slug" component={DynamicDoc} />

          {/* Fallback */}
          <Route component={NotFound} />
        </Switch>
        </Suspense>
        </PageLoadBoundary>
      </main>
      {!immersive && (isCompactFooter(location) ? <DesertFooter /> : desert ? <DesertFooter full /> : <SiteFooter />)}
    </>
  );
}

const PAGE_LOADERS = [
  { path: "/find-your-path", page: FindYourPath },
  { path: "/", page: Arrival },
  { path: "/home", page: Home },
  { path: "/start", page: JourneyStart },
  { path: "/about", page: About },
  { path: "/btc", page: Btc },
  { path: "/trust", page: Trust },
  { path: "/two-gates", page: TwoGates },
  { path: "/audit", page: Audit },
  { path: "/audit/culture", page: Audit },
  { path: "/contributors", page: Contributors },
  { path: "/in-motion/recovery", page: InMotionRecovery },
  { path: "/in-motion/blocks", page: InMotionBlocks },
  { path: "/learn", page: Learn },
  { path: "/learn/trust-tiers", page: TrustTiers },
  { path: "/learn/lineage", page: Lineage },
  { path: "/learn/continuity", page: Continuity },
  { path: "/learn/constitution", page: Constitution },
  { path: "/learn/simulator", page: DryDock },
  { path: "/learn/trust-force-equation", page: TrustForceEquation },
  { path: "/learn/coordinates", page: Coordinates },
  { path: "/learn/coordinates/map", page: CoordinateMapTool },
  { path: "/specs", page: Specs },
  { path: "/specs/blue-zones", page: BlueZone },
  { path: "/implement", page: Implement },
  { path: "/disarm", page: Disarm },
  { path: "/disarm/memo", page: DisarmMemo },
  { path: "/enshittification", page: Enshittification },
  { path: "/governed-autonomy", page: GovernedAutonomy },
  { path: "/governed-autonomy/enterprise", page: Enterprise },
  { path: "/governed-autonomy/operating-model", page: GaOperatingModel },
  { path: "/governed-autonomy/semantic-join", page: GaSemanticJoin },
  { path: "/governed-autonomy/knowledge-graph", page: GaKnowledgeGraph },
  { path: "/governed-autonomy/mission-join", page: GaMissionJoin },
  { path: "/governed-autonomy/work-types", page: GaWorkTypes },
  { path: "/governed-autonomy/workforce", page: GaWorkforce },
  { path: "/governed-autonomy/playbooks", page: GaPlaybooks },
  { path: "/governed-autonomy/tests", page: GaTests },
  { path: "/governed-autonomy/writing-rules", page: GaWritingRules },
  { path: "/governed-autonomy/glossary", page: GaGlossary },
  { path: "/governed-autonomy/risks", page: EnterpriseRisks },
  { path: "/governed-autonomy/architecture", page: EnterpriseArchitecture },
  { path: "/governed-autonomy/roadmap", page: EnterpriseRoadmap },
  { path: "/governed-autonomy/ciso", page: EnterpriseCiso },
  { path: "/governed-autonomy/methods", page: EnterpriseMethods },
  { path: "/governed-autonomy/skeptical", page: EnterpriseSkeptical },
  { path: "/governed-autonomy/defense-map", page: EnterpriseDefenseMap },
  { path: "/governed-autonomy/home-grown", page: EnterpriseHomeGrown },
  { path: "/research", page: Research },
  { path: "/research/roadmap", page: Roadmap },
  { path: "/research/papers", page: Papers },
  { path: "/research/methods", page: Methods },
  { path: "/research/cartography", page: Cartography },
  { path: "/research/wolfram", page: Wolfram },
  { path: "/research/cognitive", page: Cognitive },
  { path: "/research/cognitive/dcti", page: Dcti },
  { path: "/research/cognitive/installed-watcher", page: InstalledWatcher },
  { path: "/research/cognitive/sources", page: InstalledWatcherSources },
  { path: "/research/cognitive/acpp", page: Acpp },
  { path: "/research/cognitive/ladder", page: Ladder },
  { path: "/research/cognitive/lineage", page: ClassicalLineage },
  { path: "/research/cognitive/internal", page: InternalIndex },
  { path: "/research/cognitive/internal/:slug", page: InternalDoc },
  { path: "/research/falsification", page: Falsification },
  { path: "/research/falsification/bridge-memo", page: BridgeMemoS5S6 },
  { path: "/research/can-you-measure-trust", page: CanYouMeasureTrust },
  { path: "/research/shipwreck", page: Shipwreck },
  { path: "/research/violence", page: Violence },
  { path: "/research/identity-as-attractor", page: IdentityAsAttractor },
  { path: "/research/ai-2027", page: Ai2027 },
  { path: "/research/magnifica-humanitas", page: MagnificaHumanitas },
  { path: "/research/control-roadmap", page: ControlRoadmap },
  { path: "/research/exploitgym", page: ExploitGym },
  { path: "/research/agi-to-asi", page: AgiToAsi },
  { path: "/research/the-rfp", page: TheRfp },
  { path: "/research/plan-a", page: PlanAResponse },
  { path: "/research/ambient-breach", page: AmbientBreach },
  { path: "/research/ambient-breach/sound", page: AmbientBreachSound },
  { path: "/research/four-clocks", page: FourClocks },
  { path: "/research/federation", page: Federation },
  { path: "/research/scenario", page: Scenario },
  { path: "/research/quantum", page: Quantum },
  { path: "/research/cell-check", page: CellCheck },
  { path: "/research/collaborate", page: Collaborate },
  { path: "/research/prior-art", page: PriorArt },
  { path: "/research/coe-primer", page: CoePrimer },
  { path: "/research/suspicious-minds", page: SuspiciousMinds },
  { path: "/research/suspicious-minds/stage", page: SuspiciousMindsStage },
  { path: "/canon", page: Canon },
  { path: "/canon/stack", page: CanonStack },
  { path: "/canon/claims", page: CanonClaims },
  { path: "/canon/limits", page: CanonLimits },
  { path: "/canon/glossary", page: CanonGlossary },
  { path: "/canon/ethics", page: CanonEthics },
  { path: "/canon/synthesis", page: CanonSynthesis },
  { path: "/canon/repair", page: CanonRepair },
  { path: "/canon/reconciliation-dynamics", page: ReconciliationDynamics },
  { path: "/canon/clusters", page: CanonClusters },
  { path: "/canon/ancestors/stewart-brand", page: StewartBrand },
  { path: "/language", page: Language },
  { path: "/writing", page: Writing },
  { path: "/writing/authority-without-gravity", page: AuthorityWithoutGravity },
  { path: "/writing/what-was-growing", page: WhatWasGrowing },
  { path: "/writing/what-it-costs", page: WhatItCosts },
  { path: "/writing/what-remains", page: WhatRemains },
  { path: "/writing/six-seven", page: SixSeven },
  { path: "/writing/softened-ground", page: SoftenedGround },
  { path: "/writing/what-we-plant", page: WhatWePlant },
  { path: "/writing/:slug", page: WritingDoc },
  { path: "/community", page: Community },
  { path: "/voyage/:slug", page: VoyageChapter },
  { path: "/advocacy", page: Advocacy },
  { path: "/advocacy/playbook", page: AdvocacyPlaybook },
  { path: "/advocacy/child-safety-pledge", page: AdvocacyChildSafetyPledge },
  { path: "/advocacy/substrates", page: AdvocacySubstrates },
  { path: "/advocacy/family", page: AdvocacyFamily },
  { path: "/advocacy/culture", page: AdvocacyCulture },
  { path: "/advocacy/measurement", page: AdvocacyMeasurement },
  { path: "/advocacy/law", page: AdvocacyLaw },
  { path: "/advocacy/money", page: AdvocacyMoney },
  { path: "/advocacy/k12", page: AdvocacyK12Hub },
  { path: "/advocacy/k12/substrate-map", page: AdvocacyK12SubstrateMap },
  { path: "/advocacy/k12/measurement-reform", page: AdvocacyMeasurementReform },
  { path: "/advocacy/k12/phone-free-zones", page: AdvocacyPhoneFreeZones },
  { path: "/advocacy/k12/interpretive-practice", page: AdvocacyInterpretivePractice },
  { path: "/advocacy/k12/substrate-provisioning", page: AdvocacySubstrateProvisioning },
  { path: "/advocacy/k12/voice-in-design", page: AdvocacyVoiceInDesign },
  { path: "/advocacy/k12/ai-literacy/ladder", page: AdvocacyLadderCurriculum },
  { path: "/advocacy/k12/ai-literacy", page: AdvocacyAiLiteracy },
  { path: "/advocacy/k12/ai-guidance-crosswalk", page: AdvocacyAiGuidanceCrosswalk },
  { path: "/advocacy/k12/storm-scale", page: AdvocacyStormScale },
  { path: "/advocacy/sfps", page: AdvocacySfpsHub },
  { path: "/advocacy/sfps/parent-1pager", page: AdvocacySfpsParent1Pager },
  { path: "/advocacy/sfps/file-an-ipra", page: AdvocacySfpsFileIpra },
  { path: "/advocacy/sfps/thought-partner", page: AdvocacySfpsThread },
  { path: "/advocacy/sfps/clocks", page: AdvocacySfpsClocks },
  { path: "/advocacy/sfps/scorecard", page: AdvocacySfpsScorecard },
  { path: "/advocacy/sfps/proposal", page: AdvocacySfpsProposal },
  { path: "/advocacy/sfps/sources", page: AdvocacySfpsSources },
  { path: "/advocacy/sfps/amira-policy-diff", page: AdvocacySfpsAmiraPolicyDiff },
  { path: "/advocacy/sfps/lesc-hearing", page: AdvocacySfpsLescHearing },
  { path: "/advocacy/sfps/the-record", page: AdvocacySfpsTheRecord },
  { path: "/advocacy/sfps/read", page: AdvocacySfpsRead },
  { path: "/advocacy/templates", page: AdvocacyTemplates },
  { path: "/advocacy/scoreboard", page: AdvocacyScoreboard },
  { path: "/advocacy/kpis", page: AdvocacyKpis },
  { path: "/advocacy/coalition", page: AdvocacyCoalition },
  { path: "/advocacy/disclose", page: AdvocacyDisclose },
  { path: "/advocacy/contribute", page: AdvocacyContribute },
  { path: "/advocacy/decision-tree", page: AdvocacyDecisionTree },
  { path: "/advocacy/opposition", page: AdvocacyOpposition },
  { path: "/advocacy/existing-law", page: AdvocacyExistingLaw },
  { path: "/colophon", page: FakeNotFound },
  { path: "/physics", page: PhysicsHome },
  { path: "/physics/axioms", page: AxiomList },
  { path: "/physics/axioms/:n", page: Axiom },
  { path: "/physics/:segment", page: PhysicsSlugRouter },
  { path: "/learn/:slug", page: DynamicDoc },
  { path: "/specs/:slug", page: DynamicDoc },
  { path: "/schemas", page: DynamicDoc },
  { path: "/schemas/:slug", page: DynamicDoc },
  { path: "/implement/:slug", page: DynamicDoc },
  { path: "/community/:slug", page: DynamicDoc },
  { path: "/community/writings/:slug", page: DynamicDoc },
  { path: "/language/:id", page: VocabDossier },
];

export async function preloadRoute(path: string) {
  const pathname = path.split(/[?#]/)[0].replace(/\/$/, "") || "/";
  const clean = BASE && (pathname === BASE || pathname.startsWith(BASE + "/")) ? pathname.slice(BASE.length) || "/" : pathname;
  const match = PAGE_LOADERS.find((row) => {
    const pattern = row.path.split("/").map((part) => part.startsWith(":") ? "[^/]+" : part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("/");
    return new RegExp(`^${pattern}$`).test(clean);
  });
  await (match?.page ?? NotFound).preload();
  if (match?.page === DynamicDoc || match?.page === WritingDoc) {
    const [{ contentByUrl }, { loadDoc }] = await Promise.all([import("./lib/content-routing"), import("./lib/content")]);
    const doc = contentByUrl(clean);
    if (doc?.ready) await loadDoc(doc.source);
  }
}
